/* eslint-disable react-hooks/exhaustive-deps */
import dynamic from 'next/dynamic'
import React, {
  memo,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import isArray from 'lodash/isArray'
import { useInView } from 'react-intersection-observer'
import { AxiosHeaders } from 'axios'
import TimePeriodSeriesLabel from '../Common/Charts/TimePeriodSeriesLabel'
import Text from '../Common/NewText'
import ChartLoader from './ChartLoader'
import optimusPrime from '@/root/utils/network-adapter/network-adapter'
import { IN_STOCKS_CHART_URL } from '@/root/api/apiConstants'
import useInterval from '@/hooks/use-interval'
import { Candle, TimelineDatum } from '@/components/Stocks/Widgets/types'
import FullScreenIcon from '@/assets/common/fullscreen-icon.inline.svg'
import OptionChainIcon from '@/assets/common/option-chain.svg'
import { LivePriceContext } from '@/components/Stocks/Pages/INStockDetailPage/context'
import Link from '@/components/Common/Link'
import { yieldToMain } from '@/root/utils/yield-to-main'
import ReturnsData from './ReturnsData'

const initialState = {
  monthsAgo: 1,
  updatedChartData: [],
  noData: false,
  timeLineData: [],
  socketChartData: [],
  chartColor: '',
  candleStickData: [],
}

type ACTIONTYPE =
  | {
      type: 'UPDATE_MONTHAGO'
      monthsAgo: number
    }
  | {
      type: 'UPDATE_CHART_DATA'
      updatedChartData: Candle[]
      timeLineData?: TimelineDatum[]
      chartColor?: string
      candleStickData: Candle[]
    }
  | {
      type: 'UPDATE_TIMELINE_DATA'
      timeLineData: TimelineDatum[]
    }
  | {
      type: 'UPDATE_SOCKET_DATA'
      socketChartData: {
        ts: number
        close: number
      }[]
    }

function reducer(state: typeof initialState, action: ACTIONTYPE) {
  switch (action.type) {
    case 'UPDATE_CHART_DATA':
      return {
        ...state,
        updatedChartData: action.updatedChartData,
        timeLineData:
          action.timeLineData === undefined
            ? state.timeLineData
            : action.timeLineData,
        noData: !action.updatedChartData.length,
        socketChartData: [],
        chartColor:
          action.chartColor === undefined
            ? state.chartColor
            : action.chartColor,
        candleStickData: action.candleStickData,
      }

    case 'UPDATE_MONTHAGO':
      return {
        ...state,
        monthsAgo: action.monthsAgo,
      }
    case 'UPDATE_TIMELINE_DATA':
      return {
        ...state,
        timeLineData: action.timeLineData,
      }
    case 'UPDATE_SOCKET_DATA':
      return {
        ...state,
        socketChartData: action.socketChartData,
      }
    default:
      return state
  }
}

export interface INStockChartProps {
  name?: string
  companyCode?: string | string[]
  chartData?: Candle[]
  timeline_data?: TimelineDatum[] | null
  lineColor?: string
  firebase_path?: string
  trading_symbol?: string
  // candles_stick_navlink?: string
  graphLoading?: boolean
  // loggedIn?: boolean
  isMobile?: boolean | null
  headers?: unknown
  showTechnicalCta?: boolean
  isFnO?: boolean
  graphEndpointUrl?: string
  has_fno?: boolean
  is_global?: boolean
  removeCurrency?: boolean
  isMarketOpen?: boolean
}
let flag = false

const DynamicChartWithNoSSR = memo(
  dynamic(() => import('./ChartWrapper'), {
    ssr: false,
    loading: ChartLoader,
  }),
)

// eslint-disable-next-line max-lines-per-function, complexity
const INDStocksChart = ({
  name = '',
  companyCode,
  chartData = [],
  timeline_data = [],
  lineColor = '',
  firebase_path,
  trading_symbol,
  // candles_stick_navlink = '',
  graphLoading = false,
  isMobile = false,
  headers,
  showTechnicalCta,
  isFnO = false,
  graphEndpointUrl,
  has_fno,
  is_global,
  removeCurrency = false,
  isMarketOpen,
}: INStockChartProps) => {
  const { ref, inView } = useInView({ threshold: 0 })
  const router = useRouter()
  const { query, asPath } = router
  const [store, dispatch] = useReducer(reducer, initialState)
  const socketData = useContext(LivePriceContext)
  const {
    updatedChartData: chartList = chartData,
    noData = false,
    timeLineData = [],
    socketChartData,
    chartColor,
    candleStickData,
  } = store
  const updatedChartData = chartList.length ? chartList : chartData
  useState<number | null>(null)

  const [returnsData, setReturnsData] = useState<{
    absoluteReturn?: number
    absoluteReturnPercentage?: number
    returnTxt?: string
  }>({})

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(graphLoading)
  useEffect(() => {
    setTimeout(() => {
      setLoading(graphLoading)
    }, 100)
  }, [graphLoading])

  useEffect(() => {
    if (chartData.length) {
      dispatch({
        type: 'UPDATE_CHART_DATA',
        updatedChartData: chartData,
        timeLineData: timeline_data ?? [],
        candleStickData: [],
      })
    } else if (timeline_data?.length) {
      dispatch({
        type: 'UPDATE_TIMELINE_DATA',
        timeLineData: timeline_data,
      })
    }
  }, [chartData, timeline_data])

  //not using state as of now button is removed
  // const [switchChart, setSwitchChart] = useState(false)
  const [switchChart] = useState(false)
  const selectedTimeLine = timeLineData.filter(({ is_checked }) => is_checked)
  const is1D = selectedTimeLine[0]?.label === '1D'
  useEffect(() => {
    const { price = 0 } = socketData ?? {}
    if (price && is1D) {
      dispatch({
        type: 'UPDATE_SOCKET_DATA',
        socketChartData: [
          ...socketChartData,
          ...[{ ts: Date.now(), close: price }],
        ],
      })
    }
  }, [is1D, socketData])

  useEffect(() => {
    if (asPath) {
      dispatch({
        type: 'UPDATE_SOCKET_DATA',
        socketChartData: [],
      })
    }
  }, [asPath])

  const getApiUrl = payload => {
    const { start_date, end_date, label } = payload
    const BASE_URL = graphEndpointUrl
      ? graphEndpointUrl
      : `${IN_STOCKS_CHART_URL}/${companyCode}`
    let url = `${BASE_URL}?start_date=${start_date}&end_date=${end_date}&label=${label}&response_format=json`
    if (!graphEndpointUrl) {
      url += `${switchChart ? `` : ''}${isFnO ? '&segment=fno' : ''}`
    }
    return url
  }

  const getGraphData = async ({ start_date, end_date, label }) => {
    const apiUrl = getApiUrl({ start_date, end_date, label })
    const [chartDataFromAPI] = await optimusPrime<{
      candles: Array<{ ts: number; close: number }>
      lineColor: string
      absoluteReturn?: number
      absoluteReturnPercentage?: number
    }>({
      url: apiUrl,
      headers: headers as AxiosHeaders,
      timeout: 60000, //ASK Arihant why timeout is required here
    })
    dispatch({
      type: 'UPDATE_CHART_DATA',
      updatedChartData: chartDataFromAPI?.candles ?? [],
      chartColor: chartDataFromAPI?.lineColor ?? '',
      candleStickData: chartDataFromAPI?.candles ?? [],
    })

    void setReturnsData({
      absoluteReturn: chartDataFromAPI?.absoluteReturn,
      absoluteReturnPercentage: chartDataFromAPI?.absoluteReturnPercentage,
      returnTxt: label,
    })
  }

  useInterval(
    () => {
      if (is1D && selectedTimeLine.length) {
        const { start_date, end_date, label } = selectedTimeLine[0]
        void getGraphData({ start_date, end_date, label })
      }
    },
    is1D && selectedTimeLine.length && isMarketOpen ? 60000 : null,
  )
  useEffect(() => {
    if (switchChart) {
      flag = true
    }
    if (flag) {
      if (selectedTimeLine.length) {
        const { label, start_date, end_date } = selectedTimeLine[0] ?? {}
        void getGraphData({ label, start_date, end_date })
      }
    }
  }, [switchChart])

  const labelRefs = useRef<HTMLDivElement[]>([])

  useEffect(() => {
    labelRefs.current = labelRefs.current.slice(0, timeLineData.length)
  }, [timeLineData])

  const selectedLabel = timeLineData.find(item => item.is_checked)?.label

  const selectedLabelRef = labelRefs.current.find(
    ref => ref.innerText === selectedLabel,
  )

  const navlink = `/stocks/${query.stockSlug}/technicalchart`
  const optionChainNavlink = `/options/${query.stockSlug}`

  return (
    <div className="bg-brand-white">
      <div className="mb-2.5 flex flex-row items-center justify-between">
        <ReturnsData returnsData={returnsData} />
        {(has_fno || showTechnicalCta) && (
          <div className="flex w-full flex-row items-center justify-end gap-1">
            {has_fno && (
              <Link href={optionChainNavlink} title="Option Chain">
                <Text
                  appearance="caption"
                  addClassName="px-1.5 text-chart-blue bg-brand-grey-background md:py-0.5 rounded-2xl flex items-center"
                >
                  <OptionChainIcon className="h-6 rotate-45" />
                  <span className="hidden md:block"> Option Chain</span>
                </Text>
              </Link>
            )}
            {showTechnicalCta && (
              <Link href={navlink} title="Tech Chart">
                <Text
                  appearance="caption"
                  addClassName="px-1.5 text-chart-blue bg-brand-grey-background md:py-0.5 rounded-2xl flex items-center"
                >
                  <FullScreenIcon className="h-6 w-6" />
                  Tech Chart
                </Text>
              </Link>
            )}
          </div>
        )}
      </div>
      {graphLoading && <ChartLoader />}
      {!graphLoading && (
        <div ref={ref} className="">
       
            <div>
              {noData || !updatedChartData.length ? (
                <div
                  className={classNames(
                    'flex h-[220px] flex-col items-center justify-center text-white md:h-[350px]',
                  )}
                >
                  <div className="flex w-full flex-col">
                    <div className=" bg-brand-grey-base  mx-auto rounded-[48px] px-3 py-1">
                      <Text appearance="overline2">
                        CHART DATA NOT AVAILABLE
                      </Text>
                    </div>
                    <div />
                  </div>
                </div>
              ) : (
                <div>
                    <DynamicChartWithNoSSR
                      chartData={updatedChartData}
                      chartList={chartList}
                      lineColor={lineColor}
                      timeLineData={timeLineData}
                      chartColor={chartColor}
                      socketChartData={socketChartData}
                      isMobile={isMobile}
                      name={name}
                      is_global={is_global}
                      removeCurrency={removeCurrency}
                    />
                </div>
              )}
            </div>

        </div>
      )}
      {graphLoading || noData ? (
        <div className="my-3 h-7 w-full" />
      ) : (
        <div className="flex items-center justify-between">
          <div />
          <div
            className="relative my-1 flex w-full flex-row items-center justify-center gap-x-1
          lg:gap-x-2.5"
          >
            {!!timeLineData.length &&
              timeLineData.map(
                (
                  {
                    label = '',
                    start_date = '',
                    end_date = '',
                    is_checked = false,
                  },
                  idx,
                ) => (
                  <div
                    key={label}
                    ref={ref => {
                      if (ref) {
                        labelRefs.current[idx] = ref
                      }
                    }}
                  >
                    <TimePeriodSeriesLabel
                      selected={is_checked}
                      label={label}
                      onTimePeriodSelected={async () => {
                        await yieldToMain()
                        dispatch({
                          type: 'UPDATE_TIMELINE_DATA',
                          timeLineData: isArray(timeline_data)
                            ? timeline_data.map(item => {
                                return {
                                  ...item,
                                  is_checked: label === item.label,
                                }
                              })
                            : [],
                        })
                        void getGraphData({ start_date, end_date, label })
                      }}
                    />
                  </div>
                ),
              )}

            <div
              className="absolute bottom-0 top-0 z-10 rounded-3xl  bg-blue-500 opacity-[15%] transition-all duration-300 "
              style={{
                width: `${selectedLabelRef?.offsetWidth}px`,
                left: `${selectedLabelRef?.offsetLeft}px`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default memo(INDStocksChart)
